import { useState } from "react";
import { Button, Input, Modal, Select, showToast } from "@xpense/ui";
import { api, Transaction } from "../api/client";

const TXN_TYPE_OPTIONS = [
  { value: "expense", label: "Expense" },
  { value: "income", label: "Income" },
  { value: "transfer", label: "Transfer" },
  { value: "investment", label: "Investment" },
  { value: "loan", label: "Loan" },
  { value: "reimbursement", label: "Reimbursement" },
];

const PAYMENT_MODE_OPTIONS = [
  { value: "upi", label: "UPI" },
  { value: "card", label: "Card" },
  { value: "netbanking", label: "Net Banking" },
  { value: "cash", label: "Cash" },
  { value: "wallet", label: "Wallet" },
  { value: "emi", label: "EMI" },
  { value: "bank_transfer", label: "Bank Transfer" },
  { value: "cheque", label: "Cheque" },
  { value: "unknown", label: "Unknown" },
];

interface EditTransactionModalProps {
  transaction: Transaction;
  open: boolean;
  onClose: () => void;
  onSaved: (updated: Transaction) => void;
}

export default function EditTransactionModal({
  transaction,
  open,
  onClose,
  onSaved,
}: EditTransactionModalProps) {
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(() => ({
    merchant: transaction.merchant || "",
    amount: String(transaction.amount),
    txn_type: transaction.txn_type,
    category: transaction.category || "",
    subcategory: transaction.subcategory || "",
    payment_mode: transaction.payment_mode || "",
    account_last4: transaction.account_last4 || "",
    timestamp: toDateTimeLocal(transaction.timestamp),
    user_notes: transaction.user_notes || "",
  }));

  const set = (field: string, value: string) =>
    setForm((prev) => ({ ...prev, [field]: value }));

  const isDirty = (field: string, originalValue: unknown): boolean => {
    const current = (form as Record<string, string>)[field];
    const orig = originalValue === null || originalValue === undefined ? "" : String(originalValue);
    if (field === "timestamp") {
      return current !== toDateTimeLocal(transaction.timestamp);
    }
    return current !== orig;
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const patchBody: Record<string, unknown> = {};

      if (isDirty("amount", transaction.amount)) {
        patchBody.amount = parseFloat(form.amount);
      }
      if (isDirty("merchant", transaction.merchant)) {
        patchBody.merchant = form.merchant || null;
      }
      if (isDirty("category", transaction.category)) {
        patchBody.category = form.category || null;
      }
      if (isDirty("subcategory", transaction.subcategory)) {
        patchBody.subcategory = form.subcategory || null;
      }
      if (isDirty("payment_mode", transaction.payment_mode)) {
        patchBody.payment_mode = form.payment_mode || null;
      }
      if (isDirty("txn_type", transaction.txn_type)) {
        patchBody.txn_type = form.txn_type;
      }
      if (isDirty("account_last4", transaction.account_last4)) {
        patchBody.account_last4 = form.account_last4 || null;
      }
      if (isDirty("timestamp", transaction.timestamp)) {
        patchBody.timestamp = new Date(form.timestamp).toISOString();
      }
      if (isDirty("user_notes", transaction.user_notes)) {
        patchBody.user_notes = form.user_notes || null;
      }

      if (Object.keys(patchBody).length === 0) {
        onClose();
        return;
      }

      const updated = await api.patch<Transaction>(
        `/api/v1/transactions/${transaction.id}`,
        patchBody
      );

      showToast("Transaction updated", "success");
      onSaved(updated);
    } catch (err) {
      showToast(err instanceof Error ? err.message : "Failed to update transaction", "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Edit Transaction"
      footer={
        <>
          <Button variant="secondary" size="sm" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button variant="primary" size="sm" onClick={handleSave} loading={saving}>
            Save Changes
          </Button>
        </>
      }
    >
      <div style={{ display: "flex", flexDirection: "column", gap: "var(--xpn-space-md)" }}>
        <Input
          label="Merchant"
          value={form.merchant}
          onChange={(e) => set("merchant", e.target.value)}
          placeholder="e.g. Swiggy, Amazon"
        />

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "var(--xpn-space-md)" }}>
          <Input
            label="Amount"
            type="number"
            step="0.01"
            min="0"
            value={form.amount}
            onChange={(e) => set("amount", e.target.value)}
          />
          <Input
            label="Currency"
            value="INR"
            disabled
            hint="Read-only"
          />
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "var(--xpn-space-md)" }}>
          <Select
            label="Type"
            options={TXN_TYPE_OPTIONS}
            value={form.txn_type}
            onChange={(v: string) => set("txn_type", v)}
          />
          <Select
            label="Payment Mode"
            options={PAYMENT_MODE_OPTIONS}
            value={form.payment_mode}
            onChange={(v: string) => set("payment_mode", v)}
            placeholder="Select payment mode"
          />
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "var(--xpn-space-md)" }}>
          <Input
            label="Category"
            value={form.category}
            onChange={(e) => set("category", e.target.value)}
            placeholder="e.g. Food, Transport"
          />
          <Input
            label="Subcategory"
            value={form.subcategory}
            onChange={(e) => set("subcategory", e.target.value)}
            placeholder="e.g. Pizza, Metro"
          />
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "var(--xpn-space-md)" }}>
          <Input
            label="Account Last 4"
            maxLength={4}
            value={form.account_last4}
            onChange={(e) => set("account_last4", e.target.value)}
            placeholder="e.g. 4242"
          />
          <Input
            label="Timestamp"
            type="datetime-local"
            value={form.timestamp}
            onChange={(e) => set("timestamp", e.target.value)}
          />
        </div>

        <Input
          label="Notes"
          value={form.user_notes}
          onChange={(e) => set("user_notes", e.target.value)}
          placeholder="Add any notes..."
        />
      </div>
    </Modal>
  );
}

function toDateTimeLocal(iso: string): string {
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
